import { useId, useMemo, useState } from "react";
import { useMeasure } from "@/hooks/use-measure";

/**
 * A small multi-series line chart on a fixed ordinal scale — built for the
 * review trend (WI-261) but deliberately knowing nothing about reviews.
 *
 * Two things it does that a generic line chart does not:
 *
 *  - **A null point breaks the line.** Nothing is interpolated across a period
 *    that was never measured; the gap is the honest reading.
 *  - **A `break` breaks it too**, drawn as a marked discontinuity. The points
 *    either side are both real, but something changed between them that makes
 *    comparing them meaningless, and a slope would assert a change that was
 *    never observed.
 *
 * Hand-built SVG in the house `DonutChart` / `HeadcountTrendCard` idiom: hover
 * crosshair and tooltip, with an sr-only data table as the non-visual
 * fallback. Identity never rests on colour alone — every series carries a dash
 * pattern and is named in both the legend and the tooltip.
 */

const PAD = { top: 12, right: 14, bottom: 26, left: 30 };

export interface TrendLineSeries {
  key: string;
  label: string;
  /** A full CSS colour value, typically a design token. */
  color: string;
  dash?: string;
  /** One entry per tick, aligned with `ticks`. Null is "not measured". */
  points: (number | null)[];
  /** Extra wording for the legend and tooltip, e.g. an anonymity note. */
  note?: string;
}

export interface TrendLineTick {
  /** Stable identity for this tick — never its label, which may repeat. */
  key: string;
  /** Short, and distinct from its neighbours — this is the axis label. */
  label: string;
  /** The full name, shown in the tooltip and the data table. */
  title: string;
}

export interface TrendLineBreak {
  /** The break sits between this tick and the next. */
  afterIndex: number;
  reason: string;
}

interface Geometry {
  width: number;
  height: number;
  plotHeight: number;
  x: number[];
  gridLines: { value: number; y: number }[];
  breakLines: { x: number; reason: string }[];
  paths: { key: string; d: string; color: string; dash?: string }[];
  dots: { key: string; cx: number; cy: number; color: string }[];
}

function yFor(value: number, min: number, max: number, plotHeight: number): number {
  const span = max - min || 1;
  return PAD.top + plotHeight - ((value - min) / span) * plotHeight;
}

/**
 * Contiguous runs of measured points, cut wherever the line must not be drawn:
 * a null reading, or a break between two ticks.
 */
function runsOf(points: (number | null)[], breakAfter: Set<number>): number[][] {
  const runs: number[][] = [];
  let current: number[] = [];
  points.forEach((point, index) => {
    if (point === null) {
      if (current.length > 0) runs.push(current);
      current = [];
      return;
    }
    current.push(index);
    if (breakAfter.has(index)) {
      runs.push(current);
      current = [];
    }
  });
  if (current.length > 0) runs.push(current);
  // A run of one has no line to draw; its dot is rendered separately.
  return runs.filter((run) => run.length > 1);
}

export function TrendLines({
  ticks,
  series,
  breaks = [],
  min = 1,
  max = 5,
  height = 220,
  tableLabel,
  showBreakCaptions = true,
}: {
  ticks: TrendLineTick[];
  series: TrendLineSeries[];
  breaks?: TrendLineBreak[];
  min?: number;
  max?: number;
  height?: number;
  tableLabel: string;
  /**
   * The written reason under the plot. Off for small multiples that sit under
   * a chart already carrying it — the same sentence five times is noise, and
   * the marked line still shows WHERE the break is.
   */
  showBreakCaptions?: boolean;
}) {
  const titleId = useId();
  const [hover, setHover] = useState<number | null>(null);
  const { ref: plotRef, size } = useMeasure<HTMLDivElement>();

  const geometry = useMemo<Geometry | null>(() => {
    if (size.width < 120 || ticks.length === 0) return null;
    const width = size.width;
    const plotWidth = width - PAD.left - PAD.right;
    const plotHeight = height - PAD.top - PAD.bottom;
    const step = ticks.length > 1 ? plotWidth / (ticks.length - 1) : 0;
    const x = ticks.map((_, i) => PAD.left + (ticks.length > 1 ? i * step : plotWidth / 2));

    const gridLines: Geometry["gridLines"] = [];
    for (let value = min; value <= max; value++) {
      gridLines.push({ value, y: yFor(value, min, max, plotHeight) });
    }

    const breakAfter = new Set(breaks.map((b) => b.afterIndex));
    const breakLines = breaks.flatMap((b) => {
      const left = x[b.afterIndex];
      const right = x[b.afterIndex + 1];
      if (left === undefined || right === undefined) return [];
      return [{ x: (left + right) / 2, reason: b.reason }];
    });

    const paths: Geometry["paths"] = [];
    const dots: Geometry["dots"] = [];
    // Drawn back to front, so the series listed FIRST paints last. Two sources
    // that rated identically sit exactly on top of each other — common when a
    // manager and an employee agree — and whichever paints last is the only
    // one you see. Self leads the list and is dashed, so putting it on top
    // lets the solid line underneath show through its gaps instead of one
    // simply vanishing, which would read as "never rated".
    for (const s of [...series].reverse()) {
      for (const [runIndex, run] of runsOf(s.points, breakAfter).entries()) {
        const d = run
          .map((i, n) => {
            const point = s.points[i];
            const px = x[i];
            if (point === undefined || point === null || px === undefined) return "";
            return `${n === 0 ? "M" : "L"} ${px} ${yFor(point, min, max, plotHeight)}`;
          })
          .join(" ")
          .trim();
        if (d) paths.push({ key: `${s.key}-${runIndex}`, d, color: s.color, dash: s.dash });
      }
      s.points.forEach((point, i) => {
        const px = x[i];
        if (point === null || px === undefined) return;
        dots.push({
          key: `${s.key}-${i}`,
          cx: px,
          cy: yFor(point, min, max, plotHeight),
          color: s.color,
        });
      });
    }

    return { width, height, plotHeight, x, gridLines, breakLines, paths, dots };
  }, [size.width, ticks, series, breaks, min, max, height]);

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!geometry) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    geometry.x.forEach((candidate, i) => {
      const distance = Math.abs(candidate - px);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    setHover(best);
  }

  const hoveredX = hover !== null ? geometry?.x[hover] : undefined;

  return (
    <div>
      <ul className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {series.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-muted-foreground text-xs">
            <svg width="22" height="8" role="presentation" aria-hidden="true">
              <line
                x1="1"
                y1="4"
                x2="21"
                y2="4"
                stroke={s.color}
                strokeWidth="2.5"
                strokeDasharray={s.dash}
              />
            </svg>
            {s.label}
            {s.note ? ` · ${s.note}` : ""}
          </li>
        ))}
      </ul>

      <div ref={plotRef} className="relative" style={{ height }}>
        {geometry && (
          <svg
            width={geometry.width}
            height={geometry.height}
            viewBox={`0 0 ${geometry.width} ${geometry.height}`}
            className="absolute inset-0"
            role="img"
            aria-labelledby={titleId}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            <title id={titleId}>{tableLabel}</title>

            {geometry.gridLines.map((line) => (
              <g key={line.value}>
                <line
                  x1={PAD.left}
                  y1={line.y}
                  x2={geometry.width - PAD.right}
                  y2={line.y}
                  stroke="var(--border)"
                  strokeWidth="1"
                />
                <text
                  x={PAD.left - 7}
                  y={line.y + 3.5}
                  textAnchor="end"
                  className="fill-muted-foreground"
                  fontSize="10"
                >
                  {line.value}
                </text>
              </g>
            ))}

            {/* A break in comparability: the lines stop either side of it. */}
            {geometry.breakLines.map((line) => (
              <line
                key={line.reason}
                x1={line.x}
                y1={PAD.top}
                x2={line.x}
                y2={PAD.top + geometry.plotHeight}
                stroke="var(--warning)"
                strokeWidth="2"
                strokeDasharray="4 4"
              />
            ))}

            {geometry.paths.map((path) => (
              <path
                key={path.key}
                d={path.d}
                fill="none"
                stroke={path.color}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={path.dash}
              />
            ))}

            {geometry.dots.map((dot) => (
              <circle
                key={dot.key}
                cx={dot.cx}
                cy={dot.cy}
                r="3.75"
                fill={dot.color}
                stroke="var(--card)"
                strokeWidth="2"
              />
            ))}

            {hoveredX !== undefined && (
              <line
                x1={hoveredX}
                y1={PAD.top}
                x2={hoveredX}
                y2={PAD.top + geometry.plotHeight}
                stroke="var(--muted-foreground)"
                strokeWidth="1"
                opacity="0.4"
              />
            )}

            {ticks.map((tick, i) => {
              const px = geometry.x[i];
              if (px === undefined) return null;
              return (
                <text
                  key={tick.key}
                  x={px}
                  y={geometry.height - 6}
                  // The end labels sit on the plot edge; centring them there
                  // puts half the text outside the viewBox and clips it.
                  textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"}
                  className="fill-muted-foreground"
                  fontSize="10"
                >
                  {tick.label}
                </text>
              );
            })}
          </svg>
        )}

        {geometry && hover !== null && hoveredX !== undefined && (
          <div
            className="pointer-events-none absolute z-10 max-w-56 rounded-md border bg-popover px-2.5 py-1.5 text-popover-foreground shadow-md"
            style={{
              // Inside the plot, not above it. Lifting it clear of the top edge
              // (translateY(-100%)) put it outside the card, where it was
              // clipped — this chart stacks several series, so there is never
              // room above the plot the way a single-series one has.
              left: Math.min(Math.max(hoveredX, 70), geometry.width - 70),
              top: PAD.top,
              transform: "translate(-50%, 0)",
            }}
          >
            <div className="font-medium text-xs">{ticks[hover]?.title}</div>
            {series.map((s) => (
              <div key={s.key} className="text-muted-foreground text-xs">
                {s.label}:{" "}
                {s.points[hover] === null || s.points[hover] === undefined
                  ? "not rated"
                  : (s.points[hover] ?? 0).toFixed(1)}
              </div>
            ))}
            {breaks
              .filter((b) => b.afterIndex === hover || b.afterIndex + 1 === hover)
              .map((b) => (
                <div key={b.reason} className="mt-1 text-warning-foreground text-xs">
                  {b.reason}
                </div>
              ))}
          </div>
        )}
      </div>

      <table className="sr-only" aria-label={tableLabel}>
        <thead>
          <tr>
            <th>Cycle</th>
            {series.map((s) => (
              <th key={s.key}>{s.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ticks.map((tick, i) => (
            <tr key={tick.key}>
              <td>{tick.title}</td>
              {series.map((s) => (
                <td key={s.key}>{s.points[i] === null ? "not rated" : s.points[i]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {showBreakCaptions && breaks.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {breaks.map((b) => (
            <li key={b.reason} className="text-muted-foreground text-xs">
              {ticks[b.afterIndex]?.title} → {ticks[b.afterIndex + 1]?.title}: {b.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
